import {
	EntertainerApplicationContract,
	EntertainerApplicationFieldErrors,
	EntertainerApplicationFormState,
} from './EntertainerApplicationTypes';
import { isValidAustralianPhone, validateAustralianAddressAndAbn } from 'shared-components';
import { formatList, formatMegabytes, promotionLinkFields } from './EntertainerApplicationFormUtils';

export function validateEntertainerApplication(
	form: EntertainerApplicationFormState,
	contract: EntertainerApplicationContract,
) {
	const errors: EntertainerApplicationFieldErrors = {};
	const { actFormats, genres, specialties, performerCount, powerSlots } = contract.constraints;

	if (!form.EntertainerName.trim()) errors.EntertainerName = 'Enter the name of your act.';
	if (!form.ContactFirstName.trim()) errors.ContactFirstName = 'Enter the contact first name.';
	if (!form.ContactLastName.trim()) errors.ContactLastName = 'Enter the contact last name.';
	if (!/^\S+@\S+\.\S+$/.test(form.ContactEmailAddress.trim())) {
		errors.ContactEmailAddress = 'Enter a valid email address.';
	}
	if (!isValidAustralianPhone(form.ContactPrimaryPhoneNumber)) {
		errors.ContactPrimaryPhoneNumber = 'Enter a valid 10-digit Australian phone number.';
	}
	const addressErrors = validateAustralianAddressAndAbn({
		streetAddress1: form.EntertainerStreetAddress1,
		streetAddress2: form.EntertainerStreetAddress2,
		addressCity: form.EntertainerAddressCity,
		addressState: form.EntertainerAddressState,
		addressPostcode: form.EntertainerAddressPostcode,
		abn: form.EntertainerABN,
	});
	const addressFieldMap = {
		streetAddress1: 'EntertainerStreetAddress1',
		streetAddress2: 'EntertainerStreetAddress2',
		addressCity: 'EntertainerAddressCity',
		addressState: 'EntertainerAddressState',
		addressPostcode: 'EntertainerAddressPostcode',
		abn: 'EntertainerABN',
	} as const;
	Object.entries(addressErrors).forEach(([field, message]) => {
		errors[addressFieldMap[field as keyof typeof addressFieldMap]] = message;
	});
	if (!promotionLinkFields.some((field) => form[field].trim())) {
		errors.EntertainerWebsite = 'Provide at least one website or social profile.';
	}
	if (!form.EntertainerDescription.trim()) {
		errors.EntertainerDescription = 'Describe your act and what audiences can expect.';
	}
	if (
		form.EntertainerActFormats.length < actFormats.minSelections ||
		form.EntertainerActFormats.length > actFormats.maxSelections
	) {
		errors.EntertainerActFormats = `Select between ${actFormats.minSelections} and ${actFormats.maxSelections} act formats.`;
	}
	if (form.EntertainerGenres.length > genres.maxSelections) {
		errors.EntertainerGenres = `Select no more than ${genres.maxSelections} genres.`;
	}
	if (form.EntertainerSpecialties.length > specialties.maxSelections) {
		errors.EntertainerSpecialties = `Select no more than ${specialties.maxSelections} specialties.`;
	}
	if (
		genres.requiredForActFormats.some((format) => form.EntertainerActFormats.includes(format)) &&
		form.EntertainerGenres.length === 0
	) {
		errors.EntertainerGenres = 'Select at least one genre for live music.';
	}

	const minimum = Number(form.EntertainerMinimumPerformerCount);
	const maximum = Number(form.EntertainerMaximumPerformerCount);
	if (!Number.isInteger(minimum) || minimum < performerCount.minimum || minimum > performerCount.maximum) {
		errors.EntertainerMinimumPerformerCount =
			form.performerRangeMode === 'one-size'
				? 'Select the number of performers.'
				: 'Select the smallest act size.';
	}
	if (
		!Number.isInteger(maximum) ||
		maximum < minimum ||
		(form.performerRangeMode === 'flexible-range' && maximum === minimum) ||
		maximum > performerCount.maximum
	) {
		errors.EntertainerMaximumPerformerCount = 'Select a largest lineup above the minimum.';
	}

	const selectedPowerSlots = Number(form.EntertainerPowerSlotsRequired);
	if (
		!Number.isInteger(selectedPowerSlots) ||
		selectedPowerSlots < powerSlots.minimum ||
		selectedPowerSlots > powerSlots.maximum
	) {
		errors.EntertainerPowerSlotsRequired = `Select between ${powerSlots.minimum} and ${powerSlots.maximum} power outlets.`;
	}
	if (form.EntertainerAvailability.length === 0) {
		errors.EntertainerAvailability = 'Select at least one market or event.';
	}

	const performancePreference = contract.performancePreferences.find(
		(preference) => String(preference.value) === form.EntertainerPerformanceType,
	);
	if (!performancePreference) {
		errors.EntertainerPerformanceType = 'Select a paid or busking preference.';
	} else if (
		performancePreference.requiresPaidMarket &&
		!contract.markets.some(
			(market) => market.supportsPaidPerformance && form.EntertainerAvailability.includes(market.id),
		)
	) {
		errors.EntertainerPerformanceType = 'Select at least one market that offers paid performances.';
	}

	if (!form.customFile) {
		errors.customFile = 'Choose an image of your act.';
	} else {
		const extension = form.customFile.name.split('.').pop()?.toLowerCase() ?? '';
		if (!contract.image.acceptedExtensions.includes(extension)) {
			errors.customFile = `Choose an image in one of these formats: ${formatList(contract.image.acceptedFormats)}.`;
		} else if (form.customFile.size > contract.image.maxBytes) {
			errors.customFile = `Choose an image that is ${formatMegabytes(contract.image.maxBytes)} or smaller.`;
		}
	}

	return errors;
}
